// @ts-strict-ignore
import * as React from 'react';
import { displayUserNameLong } from '../helpers/displayHelpers';
import Modal from 'antd/lib/modal';
import ConfigProvider from 'antd/lib/config-provider';
import { useForm } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Text from 'antd/lib/typography/Text';
import Select from 'antd/lib/select';
import DatePicker from 'antd/lib/date-picker';
import antdTheme from 'antd/lib/theme';
import { useSelector } from 'react-redux';
import { CreateEditReleaseType, NewReleaseRequest, UpdateReleaseRequest } from '../actions/release';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useAppSettings } from '@/utils/hooks/helpers';
import { AppComponentProps, AppRouteComponentMatchParams, Nullable } from '../typescriptHelpers';
import { RootState } from '../reducers/reducers';
import {
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsOTAPlatform,
    isAppPlatform,
    releaseTypeToCreateEditReleaseType,
} from '../helpers';
import Divider from 'antd/lib/divider';
import { formatDateWithTimezone } from '../timeHelpers';
import { dayjs } from '@/extendedDayjs';
import { createAppRoute, createAppSettingsRouteWithSettingType } from '../helpers/routeHelpers';
import { flatMap } from 'lodash';
import {
    selectAppPlatform,
    selectAppSettings,
    selectAppStoreType,
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectIntegrationForCapability,
    selectKickedOffAt,
    selectOrgUser,
    selectRelease,
    selectReleasePilotEligibleUsersForApp,
} from '../selectors/selectors';
import { globalMessage } from '../utils/globalMessage';
import {
    IntegrationCapability,
    PatternToken,
    ReleaseScheduleCadenceStyle,
    ReleaseType,
    SettingType,
    SubscriptionFeatureType,
} from '../types';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import Alert from 'antd/lib/alert';
import Form from 'antd/lib/form';
import { FROM_ROTATION } from '../constants';
import IntegrationIcon from './icons/IntegrationIcon';
import Space from 'antd/lib/space';
import { antGrey, getAntdThemeTokens, runwayColors, titleStyle } from './styles/globalStyles';
import { useVersionValidation } from '../utils/hooks/useVersionValidation';
import { usePredictVersion } from '../utils/hooks/usePredictVersion';
import { useReleaseCreation } from '../utils/hooks/useReleaseCreation';
import { ReleaseVersionFormItem } from './prepareRelease/ReleaseVersionFormItem';
import { ReleaseNameFormItem } from './prepareRelease/ReleaseNameFormItem';
import { MatchPhasedReleaseCheckbox } from './prepareRelease/MatchPhasedReleaseCheckbox';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';
import { useThemeMode } from './styles/themeMode';
import type { DatePickerProps } from 'antd/lib/date-picker';
interface CreateEditReleaseModalProps extends AppComponentProps {
    open: boolean;
    releaseId?: string;
    isEditingRelease: boolean;
    displayOnlyTargetDates: boolean;
    onDidCreateOrEditRelease: () => void;
    onCancel: () => void;
    canBeSubmitted: Nullable<boolean>;
    onEditTargetDates?: () => void;
}

const DefaultDatePicker = ({ use24HourTimeFormat, ...props }: { use24HourTimeFormat: boolean } & DatePickerProps) => (
    <DatePicker
        {...props}
        needConfirm={false}
        showTime={{
            defaultValue: dayjs('12:00', 'HH:mm'),
            showSecond: false,
            format: use24HourTimeFormat ? 'HH:mm' : 'h:mm A',
        }}
        showNow={false}
        format={
            use24HourTimeFormat ?
                ['MM/DD [at] HH:mm [GMT]Z', 'MM/DD [at] HH:mm']
            :   ['MM/DD [at] h:mm A [GMT]Z', 'MM/DD [at] h:mm A']
        }
        minuteStep={5}
    />
);

const CreateEditReleaseModal = (props: CreateEditReleaseModalProps) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const history = useHistory();
    const { mode } = useThemeMode();

    const release = useSelector((state: RootState) => {
        if (props.releaseId && props.isEditingRelease) {
            return selectRelease(state, props.releaseId);
        }
        return null;
    });

    const kickedOffAt = useSelector((state: RootState) => {
        if (props.isEditingRelease && props.releaseId) {
            return selectKickedOffAt(state, props.releaseId);
        } else {
            return null;
        }
    });

    const [formValid, setFormValid] = React.useState(true);
    const [predictedPilotId, setPredictedPilotId] = React.useState<string | null>(null);
    const [showOverridePilotAlert, setShowOverridePilotAlert] = React.useState(false);

    const [form] = useForm();

    const appSettings = useAppSettings(props);
    const scheduleSettings = appSettings.releaseScheduleCadenceSettings;

    const scheduleIsActive =
        !!scheduleSettings && scheduleSettings.scheduleCadence !== ReleaseScheduleCadenceStyle.Never;

    const scheduleIntegration = useSelector((state: RootState) =>
        selectIntegrationForCapability(state, props.appId, IntegrationCapability.Scheduling),
    );

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const pilotUsers = useSelector((state: RootState) => selectReleasePilotEligibleUsersForApp(state, props.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));
    const {
        createReleaseSuccess: draftNewReleaseSuccess,
        createReleaseError: draftNewReleaseFailure,
        updateReleaseSuccess: editReleaseSuccess,
        updateReleaseError: editReleaseFailure,
        isLoading,
        createNewRelease: dispatchCreateNewRelease,
        updateRelease: dispatchUpdateRelease,
    } = useReleaseCreation({
        orgId: props.orgId,
        appId: props.appId,
        releaseId: props.releaseId,
    });

    const {
        validateVersion: doValidateVersion,
        resultIsStaticBranch,
        releasePilotFromRotation,
        isReleasePilotFromRotationScheduleIntegration,
    } = useVersionValidation({
        orgId: props.orgId,
        appId: props.appId,
        releaseId: props.releaseId,
        releaseType: CreateEditReleaseType.Standard,
    });

    const { predictVersion } = usePredictVersion({
        orgId: props.orgId,
        appId: props.appId,
    });

    const predictedPilotUser = useSelector((state: RootState) => selectOrgUser(state, props.orgId, predictedPilotId));

    React.useEffect(() => {
        if (releasePilotFromRotation) {
            setPredictedPilotId(releasePilotFromRotation);
        }
    }, [releasePilotFromRotation]);

    const createOrUpdateRelease = (
        isEditing: boolean,
        version?: string,
        releaseType?: CreateEditReleaseType,
        releasePilotId?: string,
        targetKickoffDate?: dayjs.Dayjs,
        targetSubmitDate?: dayjs.Dayjs,
        targetReleaseDate?: dayjs.Dayjs,
        releaseName?: string,
        shouldMatchPreviousPhasedRelease?: boolean,
    ) => {
        if (isEditing) {
            const request: UpdateReleaseRequest = {
                orgId: props.orgId,
                appId: props.appId,
                releaseId: props.releaseId,
                releaseType: releaseType,
                version: version,
                releasePilotId: releasePilotId,
                targetKickoffDate: targetKickoffDate ? targetKickoffDate.startOf('minute').toISOString() : null,
                targetSubmitDate: targetSubmitDate ? targetSubmitDate.startOf('minute').toISOString() : null,
                targetReleaseDate: targetReleaseDate ? targetReleaseDate.startOf('minute').toISOString() : null,
                releaseName: releaseName,
                shouldMatchPreviousPhasedRelease,
            };

            dispatchUpdateRelease(request);
        } else {
            const request: NewReleaseRequest = {
                orgId: props.orgId,
                appId: props.appId,
                releaseType: releaseType,
                releasePilotId: releasePilotId,
                version: version,
                targetKickoffDate: targetKickoffDate ? targetKickoffDate.startOf('hour').toISOString() : null,
                targetSubmitDate: targetSubmitDate ? targetSubmitDate.startOf('hour').toISOString() : null,
                targetReleaseDate: targetReleaseDate ? targetReleaseDate.startOf('hour').toISOString() : null,
                releaseName: releaseName,
            };

            dispatchCreateNewRelease(request);
        }
    };

    const submitForm = () => {
        const values = form.getFieldsValue();
        createOrUpdateRelease(
            props.isEditingRelease,
            values.releaseVersion,
            releaseTypeToCreateEditReleaseType(release?.type ?? ReleaseType.Minor),
            values.releasePilotId,
            values.targetKickoffDate,
            values.targetSubmitDate,
            values.targetReleaseDate,
            values.releaseName,
            values.shouldMatchPreviousPhasedRelease,
        );
    };

    const validateForm = () => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));
        const isValid = errors.length === 0;
        setFormValid(isValid);
    };

    React.useEffect(() => {
        if (editReleaseSuccess && props.onEditTargetDates) {
            props.onEditTargetDates();
        }
    }, [editReleaseSuccess, props.onEditTargetDates]);

    const handleVersionValidation = async (version: string): Promise<void> => {
        const values = form.getFieldsValue();
        const result = await doValidateVersion(version, values.sourceVersion);
        setPredictedPilotId(result.releasePilotFromRotation);
    };

    const setPredictedVersion = async (isHotfix: boolean): Promise<void> => {
        const version = await predictVersion(isHotfix);
        form.setFieldValue('releaseVersion', version);
    };

    React.useEffect(() => {
        if (props.isEditingRelease && props.open && release) {
            form.setFieldsValue({
                releaseVersion: release.version,
                targetKickoffDate:
                    release.targetKickoffDate ? dayjs(release.targetKickoffDate).tz(effectiveTimezone) : null,
                targetSubmitDate:
                    release.targetSubmitDate ? dayjs(release.targetSubmitDate).tz(effectiveTimezone) : null,
                targetReleaseDate:
                    release.targetReleaseDate ? dayjs(release.targetReleaseDate).tz(effectiveTimezone) : null,
                releaseName: release.releaseName,
                sourceVersion: release.sourceVersion,
                shouldMatchPreviousPhasedRelease: release.shouldMatchPreviousPhasedRelease,
            });

            let releasePilotId = release.releasePilotId;

            doValidateVersion(release.version, release.sourceVersion)
                .then((result) => {
                    if (result.releasePilotFromRotation && result.releasePilotFromRotation === releasePilotId) {
                        releasePilotId = FROM_ROTATION;
                    }
                })
                .finally(() => {
                    form.setFieldValue('releasePilotId', releasePilotId);
                });
        }
    }, [props.open, doValidateVersion]);

    React.useEffect(() => {
        if (props.open && !props.isEditingRelease) {
            form.setFieldsValue({
                releasePilotId: null,
                releaseVersion: null,
                targetKickoffDate: null,
                targetSubmitDate: null,
                targetReleaseDate: null,
                releaseName: null,
                sourceVersion: null,
                shouldMatchPreviousPhasedRelease: null,
            });

            setPredictedVersion(false).then(() => {
                form.validateFields();
            });
        }
    }, [props.open]);

    React.useEffect(() => {
        if (draftNewReleaseFailure && !props.isEditingRelease && props.open) {
            globalMessage.error(draftNewReleaseFailure);
        }
    }, [draftNewReleaseFailure, props.isEditingRelease, props.open]);

    React.useEffect(() => {
        if (draftNewReleaseSuccess && !props.isEditingRelease && props.open) {
            props.onDidCreateOrEditRelease();
            globalMessage.success('New release created.');
        }
    }, [draftNewReleaseSuccess, props.isEditingRelease, props.onDidCreateOrEditRelease, props.open]);

    React.useEffect(() => {
        if (editReleaseFailure && props.isEditingRelease && props.open) {
            globalMessage.error(editReleaseFailure);
        }
    }, [editReleaseFailure, props.isEditingRelease, props.open]);

    React.useEffect(() => {
        if (editReleaseSuccess && props.isEditingRelease && props.open) {
            const newVersion = form.getFieldValue('releaseVersion');
            const previousVersion = release?.version;
            const versionChanged = newVersion && newVersion !== previousVersion;

            if (versionChanged) {
                globalMessage.success('Release version and settings updated!');

                const appRoute = createAppRoute(orgIdParam, appIdParam);

                props.onDidCreateOrEditRelease();
                history.replace(appRoute);
            } else {
                globalMessage.success('Release settings updated!');

                props.onDidCreateOrEditRelease();
            }
        }
    }, [
        appIdParam,
        editReleaseSuccess,
        history,
        orgIdParam,
        props.isEditingRelease,
        props.onDidCreateOrEditRelease,
        props.open,
        release?.version,
    ]);

    const title =
        props.displayOnlyTargetDates ? 'Edit target dates'
        : props.isEditingRelease ? 'Edit release settings'
        : 'Prepare a new release';
    const buttonTitle = props.isEditingRelease && release ? `Save` : 'Prepare release';
    const disableReleaseVersion =
        props.isEditingRelease && release ? release.doesBranchExist && !release.isStaticReleaseBranch : false;

    const actualKickoffDate = kickedOffAt ? dayjs(kickedOffAt) : null;
    const submittedAt = release?.submittedAt;
    const actualSubmitDate = submittedAt ? dayjs(submittedAt) : null;
    const releasedOn = release?.releasedAt;
    const actualReleasedDate = releasedOn ? dayjs(releasedOn) : null;

    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const predictedPilotDisplayName = predictedPilotUser ? displayUserNameLong(predictedPilotUser) : null;
    const currentPilotIsRotationPilot = release?.releasePilotId ? release.releasePilotId === predictedPilotId : false;
    const scheduleIntegrationIcon =
        scheduleIntegration ? <IntegrationIcon integrationId={scheduleIntegration.id} /> : null;

    const handleSelectPilotChange = (value) => {
        if (value !== FROM_ROTATION && currentPilotIsRotationPilot) {
            setShowOverridePilotAlert(true);
        } else {
            setShowOverridePilotAlert(false);
        }
    };

    const releaseBranchPatterns = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, props.appId);

        return appSettings?.releaseBranchPatterns?.map((releaseBranchPattern) => releaseBranchPattern.pattern) ?? [];
    });

    const foundAnyBranchPatternsUsingReleaseNameToken = releaseBranchPatterns.reduce((foundAnyMatch, pattern) => {
        return foundAnyMatch ? foundAnyMatch : pattern.includes(PatternToken.ReleaseName);
    }, false);

    const disableEditReleaseName = foundAnyBranchPatternsUsingReleaseNameToken && disableReleaseVersion;

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isOTAPlatform = getIsOTAPlatform(appPlatform);
    const isHotfix = release?.type === ReleaseType.Hotfix;
    const shouldShowShouldMatchPreviousPhasedReleaseOption = props.isEditingRelease && isHotfix && isGooglePlayStore;
    const contentTheme = mode === 'dark' ? 'dark' : 'light';

    return (
        <ConfigProvider
            theme={{
                algorithm: contentTheme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                token: getAntdThemeTokens(contentTheme),
            }}
        >
            <Modal
                open={props.open}
                title={title}
                okText={buttonTitle}
                okButtonProps={{ disabled: !formValid, loading: isLoading }}
                onOk={() => form.submit()}
                confirmLoading={isLoading}
                onCancel={props.onCancel}
                afterClose={() => setShowOverridePilotAlert(false)}
                width={650}
            >
                <Form form={form} onFieldsChange={validateForm} onFinish={submitForm}>
                    <ReleaseVersionFormItem
                        disabled={disableReleaseVersion}
                        hidden={props.displayOnlyTargetDates}
                        helpText={
                            disableReleaseVersion ?
                                'You cannot change the version or hotfix selection if a release branch has already been created for this version.'
                            :   null
                        }
                        onValidate={handleVersionValidation}
                    />
                    <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.ReleasePilots}>
                        {(isDisabled) => (
                            <>
                                <FormItem
                                    label={
                                        <PlanRestrictedFeatureWrapper
                                            feature={SubscriptionFeatureType.ReleasePilots}
                                            requiresTooltip
                                        >
                                            <Text>Release pilot</Text>
                                        </PlanRestrictedFeatureWrapper>
                                    }
                                    name="releasePilotId"
                                    hidden={props.displayOnlyTargetDates}
                                    labelCol={{ flex: '130px' }}
                                    help={
                                        isReleasePilotFromRotationScheduleIntegration ?
                                            `Release pilots are being managed from ${
                                                scheduleIntegration ?
                                                    scheduleIntegration?.name
                                                :   'your incident management & scheduling integration'
                                            }`
                                        :   null
                                    }
                                >
                                    <Select
                                        placeholder="Select"
                                        allowClear
                                        disabled={isDisabled || isReleasePilotFromRotationScheduleIntegration}
                                        onChange={handleSelectPilotChange}
                                    >
                                        <Select.Option value={FROM_ROTATION}>
                                            <Space align="center">
                                                {isReleasePilotFromRotationScheduleIntegration &&
                                                    scheduleIntegrationIcon}
                                                <Text>
                                                    From rotation{' '}
                                                    {predictedPilotDisplayName != null && (
                                                        <Text type="secondary">({predictedPilotDisplayName})</Text>
                                                    )}
                                                </Text>
                                            </Space>
                                        </Select.Option>
                                        {pilotUsers.map((user) => {
                                            return (
                                                <Select.Option key={user.id} value={user.id}>
                                                    <Space align="center">
                                                        {isReleasePilotFromRotationScheduleIntegration &&
                                                            scheduleIntegrationIcon}
                                                        <Text> {displayUserNameLong(user)}</Text>
                                                    </Space>
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </FormItem>
                                {showOverridePilotAlert && (
                                    <Alert
                                        style={{ marginTop: 10, marginBottom: 16 }}
                                        type="warning"
                                        description={
                                            <>
                                                This will replace the existing pilot{' '}
                                                {predictedPilotDisplayName ?
                                                    <>
                                                        (<b>{predictedPilotDisplayName}</b>){' '}
                                                    </>
                                                :   ''}
                                                from your rotation for this release. The rotation will continue for your
                                                next release. Visit{' '}
                                                <Link
                                                    to={createAppSettingsRouteWithSettingType(
                                                        orgIdParam,
                                                        appIdParam,
                                                        SettingType.ReleasePilotRotation,
                                                    )}
                                                >
                                                    App settings {'>'} Release pilot rotation
                                                </Link>{' '}
                                                to manage your rotation.
                                            </>
                                        }
                                    />
                                )}
                            </>
                        )}
                    </PlanRestrictedFeatureWrapper>
                    <ReleaseNameFormItem disabled={disableEditReleaseName} hidden={props.displayOnlyTargetDates} />
                    {!props.displayOnlyTargetDates && <Divider />}
                    {scheduleIsActive && (
                        <>
                            <FormItem noStyle>
                                <Text
                                    style={{
                                        ...titleStyle,
                                        fontWeight: 400,
                                        fontSize: 13,
                                        marginBottom: 0,
                                        display: 'block',
                                        color: runwayColors.black,
                                    }}
                                >
                                    {isAppPlatform(appPlatform) && isAppleAppStore ?
                                        'Set target kickoff/submit/release dates'
                                    : isAppPlatform(appPlatform) && !isAppleAppStore ?
                                        'Set target kickoff and submit dates'
                                    : isOTAPlatform ?
                                        'Set target kickoff and release dates'
                                    :   'Set target kickoff date'}
                                </Text>
                            </FormItem>
                            <FormItem
                                name="noTargetDates"
                                label={
                                    <span style={{ color: antGrey.grey7, fontSize: 13 }}>
                                        {
                                            'If no target dates are set here, Runway will attempt to apply the schedule configured in App settings > Schedule.'
                                        }
                                    </span>
                                }
                                labelCol={{ span: 24 }}
                            ></FormItem>
                        </>
                    )}
                    <FormItem
                        label="Target kickoff date"
                        name="targetKickoffDate"
                        extra={
                            <span style={{ fontSize: 13 }}>
                                {actualKickoffDate ?
                                    <>
                                        Your release was kicked off on{' '}
                                        <TimeWithConditionalTz
                                            style={{ fontSize: 13, color: 'inherit' }}
                                            triggeredAtLabel={formatDateWithTimezone(
                                                actualKickoffDate,
                                                'shortWithYearAndComma',
                                                effectiveTimezone,
                                                use24HourTimeFormat,
                                            )}
                                            triggeredByMoment={actualKickoffDate}
                                            effectiveTimezone={effectiveTimezone}
                                            use24HourTimeFormat={!!use24HourTimeFormat}
                                        />
                                    </>
                                : release?.isStaticReleaseBranch === true || resultIsStaticBranch ?
                                    'Your release is kicked off when new code is detected past the last tag or promoted to the release branch by Runway.'
                                :   'Your release is kicked off when a release branch is detected or created by Runway.'
                                }
                            </span>
                        }
                    >
                        <DefaultDatePicker use24HourTimeFormat={!!use24HourTimeFormat} />
                    </FormItem>
                    {isAppPlatform(appPlatform) && (
                        <FormItem
                            label={'Target submit date'}
                            name="targetSubmitDate"
                            extra={
                                actualSubmitDate ?
                                    <span style={{ fontSize: 13 }}>
                                        Your release was submitted on{' '}
                                        <TimeWithConditionalTz
                                            style={{ fontSize: 13, color: 'inherit' }}
                                            triggeredAtLabel={formatDateWithTimezone(
                                                actualSubmitDate,
                                                'shortWithYearAndComma',
                                                effectiveTimezone,
                                                use24HourTimeFormat,
                                            )}
                                            triggeredByMoment={actualSubmitDate}
                                            effectiveTimezone={effectiveTimezone}
                                            use24HourTimeFormat={!!use24HourTimeFormat}
                                        />
                                    </span>
                                :   null
                            }
                        >
                            <DefaultDatePicker use24HourTimeFormat={!!use24HourTimeFormat} />
                        </FormItem>
                    )}
                    {(isAppleAppStore || isOTAPlatform) && (
                        <FormItem
                            label={'Target release date'}
                            name="targetReleaseDate"
                            extra={
                                <span style={{ fontSize: 13 }}>
                                    {actualReleasedDate ?
                                        <>
                                            Your release was released on{' '}
                                            <TimeWithConditionalTz
                                                style={{ fontSize: 13, color: 'inherit' }}
                                                triggeredAtLabel={formatDateWithTimezone(
                                                    actualReleasedDate,
                                                    'shortWithYearAndComma',
                                                    effectiveTimezone,
                                                    use24HourTimeFormat,
                                                )}
                                                triggeredByMoment={actualReleasedDate}
                                                effectiveTimezone={effectiveTimezone}
                                                use24HourTimeFormat={!!use24HourTimeFormat}
                                            />
                                        </>
                                    :   null}
                                </span>
                            }
                        >
                            <DefaultDatePicker use24HourTimeFormat={!!use24HourTimeFormat} />
                        </FormItem>
                    )}
                    <Divider />
                    <MatchPhasedReleaseCheckbox
                        visible={shouldShowShouldMatchPreviousPhasedReleaseOption}
                        defaultChecked={release?.shouldMatchPreviousPhasedRelease ?? false}
                        useFormItem={true}
                    />
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default CreateEditReleaseModal;
